import classNames from "classnames";
import React from "react";
import { connect } from "react-redux";

import { Control, LocalForm } from "react-redux-form";
import { Icon, Icomoon } from "../../../core/atoms/atoms";
import { SearchComponent } from "../../../core/atoms/rrf/searchComponent";
import { api } from "../../../shared/api/system/apiClient";
import { DateOfBirthPicker } from "../../../shared/components/DateOfBirthPicker";
import { ValidationBar } from "../../../shared/validationBar";
import { ActionPromises } from "../../state/actionPromises";
import ContactCard from "./contactCard";

import "./customerRequiredDetails.scss";

const IsoCountrySearch = SearchComponent as new () => SearchComponent<IIsoCountry>;

/**
 * Fields changed by the forms
 */
interface IRequiredTravelerDetails {
  enquiryId: number;
  title: Title;
  otherTitle: string;
  firstName: string;
  lastName: string;
  dateOfBirth: IDate;
  passportIssuingCountry: IIsoCountry;
  countryOfResidence?: IIsoCountry;
}

interface OwnProps {
  isLeadTraveler: boolean;
  currentStage: number;
  maxStages: number;
  onNextClicked: () => void;
  inAddMode: boolean;
  inEditMode: boolean;
  cancelAddorEdit: () => void;
  travelerInEdit: ILeadTravelerDetails | ITravelerDetails | null;
  showReviewStage: () => void;
}

interface StateProps {
  customerEnquiry: IState<IEnquiry>;
}

interface DispatchProps {
  addNewTravelerDetails: (newTraveller: INewTravelerDetailsInput) => Promise<IFetchApiResponse<IEnquiry>>;
  addNewLeadTravelerDetails: (newTraveller: INewTravelerDetailsInput) => Promise<IFetchApiResponse<IEnquiry>>;
  updateLeadTravelerDetails: (
    enquiryId: number,
    travelerDetails: INewTravelerDetailsInput
  ) => Promise<IFetchApiResponse<IEnquiry>>;
  updateTravelerDetails: (travelerDetails: Partial<ITravelerDetailsUpdate>) => Promise<IFetchApiResponse<IEnquiry>>;
}

interface CustomerRequiredDetailsComponentState {
  formData: any;
  passportIssuingCountry: IIsoCountry;
  errors?: IValidationError[];
  isSaving?: boolean;
}

type Props = OwnProps & DispatchProps & StateProps;

export class CustomerRequiredDetailsComponent extends React.Component<Props, CustomerRequiredDetailsComponentState> {

  private generateDefaultFormData(enquiryId: number): IRequiredTravelerDetails {
    return {
      enquiryId,
      title: null,
      otherTitle: null,
      firstName: null,
      lastName: null,
      dateOfBirth: null,
      passportIssuingCountry: null,
      countryOfResidence: null,
    };
  }

  public handleErrors = (err) => {
    this.setState({ errors: err.validations || [{ message: err.message } as IValidationError] });
  }

  public onNextClicked = async () => {
    const {
      formData,
      isSaving,
    } = this.state;

    if (!isSaving) {
      this.setState({ isSaving: true });
      const {
        travelerInEdit,
        onNextClicked,
      } = this.props;

      try {
        if (travelerInEdit) {
          await this.updateTraveler(formData);
        }
        else {
          await this.addNewTraveler(formData);
        }

        onNextClicked();
      }
      catch (e) {
        this.handleErrors(e as Error);
      }
      this.setState({ isSaving: false });
    }
  }

  private addNewTraveler = async (formData: IRequiredTravelerDetails) => {
    const {
      isLeadTraveler,
      addNewLeadTravelerDetails,
      addNewTravelerDetails,
    } = this.props;
    let addNew: Promise<IFetchApiResponse<IEnquiry>>;
    const { passportIssuingCountry, countryOfResidence, ...requiredFormData } = formData;

    if (isLeadTraveler) {
      addNew = addNewLeadTravelerDetails({
        ...requiredFormData,
        passportIssuingCountryId: passportIssuingCountry ? passportIssuingCountry.id : null,
        countryOfResidenceId: countryOfResidence ? countryOfResidence.id : null,
      });
    }
    else {
      addNew = addNewTravelerDetails({
        ...requiredFormData,
        passportIssuingCountryId: passportIssuingCountry ? passportIssuingCountry.id : null,
      });
    }

    return addNew;
  }

  private updateTraveler(formData: IRequiredTravelerDetails) {
    const {
      isLeadTraveler,
      updateLeadTravelerDetails,
      updateTravelerDetails,
      travelerInEdit,
    } = this.props;
    let update: Promise<IFetchApiResponse<IEnquiry>>;
    const { enquiryId, passportIssuingCountry, countryOfResidence, ...requiredFormData } = formData;

    if (isLeadTraveler) {
      update = updateLeadTravelerDetails(enquiryId, {
        ...requiredFormData,
        passportIssuingCountryId: passportIssuingCountry ? passportIssuingCountry.id : null,
        countryOfResidenceId: countryOfResidence ? countryOfResidence.id : null,
        enquiryId,
      });
    }
    else {
      update = updateTravelerDetails({
        ...requiredFormData,
        passportIssuingCountryId: passportIssuingCountry ? passportIssuingCountry.id : null,
        id: travelerInEdit.id,
      });
    }

    return update;
  }

  public componentWillMount() {
    const {
      travelerInEdit,
      customerEnquiry,
      isLeadTraveler,
    } = this.props;

    let formData = this.generateDefaultFormData(customerEnquiry.data.id);

    if (travelerInEdit) {
      const {
        title,
        otherTitle,
        firstName,
        lastName,
        dateOfBirth,
        passportIssuingCountry,
        countryOfResidence,
      } = travelerInEdit;

      formData = {
        ...formData,
        title,
        otherTitle,
        firstName,
        lastName,
        dateOfBirth,
        passportIssuingCountry,
      };

      if (isLeadTraveler) {
        formData = {
          ...formData,
          countryOfResidence,
        };
      }
    }
    this.setState({
      formData,
      errors: [],
      isSaving: false,
    });
  }

  public render() {
    if (!this.state.formData) {
      return <div>No data</div>;
    }

    const {
      inAddMode,
      inEditMode,
      cancelAddorEdit,
      isLeadTraveler,
      currentStage,
      maxStages,
      travelerInEdit,
    } = this.props;
    const {
      formData,
      errors,
    } = this.state;

    const titles = [
      { value: "mr", name: "Mr" },
      { value: "mrs", name: "Mrs" },
      { value: "master", name: "Master" },
      { value: "miss", name: "Miss" },
      { value: "ms", name: "Ms" },
      { value: "dr", name: "Dr" },
      { value: "other", name: "Other" }];

    return (
      <div className="required-details">
        <h2 className="required-details-heading">Before you book: please confirm the details of those travelling.</h2>
        {errors.length !== 0 && <ValidationBar errors={errors} />}
        {isLeadTraveler ?
          <h3 className="required-details-sub-heading">Lead Traveller</h3>
          :
          <h3 className="required-details-sub-heading">Traveller {currentStage + 1} of {maxStages}</h3>
        }
        <LocalForm
          className="required-details__form stage"
          initialState={formData}
          onChange={updated => {
            if (updated.title !== "other") {
              this.setState({ formData: { ...updated, otherTitle: null } });
            }
            else {
              this.setState({ formData: updated });
            }
          }}
        >
          <div className="columns">
            <div className={classNames("required-details__form__item", "column", "is-2")}>
              <label>Title</label>
              <Control.select
                className={classNames({
                  "required-details__form__item--title": true,
                  "placeholder": formData.title === "Title...",
                })}
                model={".title"}
                defaultValue={travelerInEdit?.title ?? "Title..."}
              >
                <option disabled className="placeholder">Title...</option>
                {titles.map(i => (
                  <option key={i.value} value={i.value}>{i.name}</option>),
                )}
              </Control.select>
            </div>
            {formData.title === "other" &&
              <div className={classNames("required-details__form__item", "column", "is-2")}>
                <label>Other title</label>
                <Control.text model=".otherTitle" placeholder="Title" />
              </div>
            }
            <div className={classNames({
              "required-details__form__item": true,
              "column": true,
              "is-4": formData.title === "other",
              "is-5": formData.title !== "other",
            })}>
              <label>First name as on passport</label>
              <Control.text model=".firstName" />
            </div>
            <div className={classNames({
              "required-details__form__item": true,
              "column": true,
              "is-4": formData.title === "other",
              "is-5": formData.title !== "other",
            })}>
              <label>Last name as on passport</label>
              <Control.text model=".lastName" />
            </div>
          </div>
          <div className="columns">
            <div className={classNames("required-details__form__item", "column", "is-6")}>
              <label>Date of birth</label>
              <DateOfBirthPicker
                className="required-details__form__item--dob"
                model=".dateOfBirth"
              />
            </div>
          </div>
          <div className="columns">
            <div className={classNames("required-details__form__item", "column", "is-6")}>
              <label>Passport issuing country</label>
              <IsoCountrySearch
                model=".passportIssuingCountry"
                getResults={async query => {
                  const results = await api.getIsoCountries(query, null);
                  return results.json.payload;
                }}
                getItemName={isoCountry => isoCountry.name}
                getItemId={isoCountry => isoCountry.id}
                inputProps={{
                  placeholder: "Type to search...",
                }}
              />
            </div>
          </div>
          {isLeadTraveler &&
            <div className="columns">
              <div className={classNames("required-details__form__item", "column", "is-6")}>
                <label>Country of residence</label>
                <IsoCountrySearch
                  model=".countryOfResidence"
                  getResults={async query => {
                    const results = await api.getIsoCountries(query, null);
                    return results.json.payload;
                  }}
                  getItemName={isoCountry => isoCountry.name}
                  getItemId={isoCountry => isoCountry.id}
                  inputProps={{
                    placeholder: "Type to search...",
                  }}
                />
              </div>
            </div>
          }
        </LocalForm>
        <div className={classNames("required-details__actions",
          { "in-review-stage": inAddMode || inEditMode },
        )}>
          {(inAddMode || inEditMode) && (
            <button type="button" className="secondary-button" onClick={cancelAddorEdit}>
              <Icon icomoon={"icon-arrow-left12" as Icomoon} />
              Back to review
            </button>
          )}

          <button type="button" className="primary-button" onClick={this.onNextClicked}>
            Confirm details
          </button>
        </div>
        <ContactCard />
      </div>
    );
  }
}

const stateToProps = (state: IWebStoreState, props: OwnProps): StateProps => {
  return {
    customerEnquiry: state.customerEnquiry,
  };
};

const dispatchToProps = (dispatch): DispatchProps => {
  return {
    addNewTravelerDetails:
      (newTraveller: INewTravelerDetailsInput) => dispatch(ActionPromises.addNewTravelerDetails(newTraveller)),
    addNewLeadTravelerDetails:
      (newTraveller: INewTravelerDetailsInput) => dispatch(ActionPromises.addNewLeadTravelerDetails(newTraveller)),
    updateLeadTravelerDetails: (
      enquiryId: number,
      travelerDetails: INewTravelerDetailsInput,
    ) => dispatch(ActionPromises.updateLeadTravelerDetails(enquiryId, travelerDetails)),
    updateTravelerDetails:
      (travellerDetails: Partial<ITravelerDetailsUpdate>) => dispatch(
        ActionPromises.updateTravelerDetails(travellerDetails)
      ),
  };
};

export const CustomerRequiredDetails = connect(stateToProps, dispatchToProps)(CustomerRequiredDetailsComponent);
